import { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Shield } from 'shield-js-npm'
import { ENDPOINTS, post } from '~/api'
import { ConfigManager, DEPLOY_ENVIRONMENT } from '~/managers/ConfigManager'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import { selectFlipperWithId } from '~/store/flippers/selectors'

const SHIELD_SITE_ID = 'e18f92c1939598061f9a30b735bff4302cc2bc78'

type ShieldSessionId = {
  id: string
  timestamp: number
}

type ShieldDeviceIntelligence = {
  session_id: ShieldSessionId
  device_intelligence: Record<string, unknown>
}

type ShieldResponse = {
  result: ShieldDeviceIntelligence
}

type ShieldError = {
  response?: {
    status: number
    data: unknown
  }
} & Error

type ShieldState = {
  isInitialized: boolean
  isLoading: boolean
  error: Error | null
}

type ShieldAttributes = {
  session_id: ShieldSessionId
  user_id: string
}

type ServerResponse = {
  result: unknown
  user_id: string
}

type ShieldProps = {
  siteId: string
  userId: number
}

async function sendAttributesToServer(attributes: ShieldAttributes): Promise<ServerResponse> {
  try {
    const response = await post(ENDPOINTS.API_SHIELD_ATTRIBUTES, attributes)
    return response as ServerResponse
  } catch (error) {
    const shieldError = error as ShieldError
    console.error('Error sending attributes:', {
      message: shieldError.message,
      response: shieldError.response,
    })
    throw error
  }
}

export const useShield = (userId?: number) => {
  const envMode = ConfigManager.getEnvironmentMode()
  const shieldRef = useRef<Shield | null>(null)
  const [state, setShieldState] = useState<ShieldState>({
    isInitialized: false,
    isLoading: false,
    error: null,
  })

  const isShieldFlipperEnabled = useSelector(selectFlipperWithId(FLIPPER_KEYS.SHIELD, ''))

  const initializeShield = useCallback(async () => {
    if (!userId) {
      console.warn('useShield: userId is required')
      return
    }

    setShieldState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      if (!shieldRef.current) {
        shieldRef.current = new Shield()
      }

      const shield = shieldRef.current
      await shield.init({
        siteId: SHIELD_SITE_ID,
        userId,
      } as ShieldProps)

      const deviceIntelligence = (await shield.getDeviceIntelligence()) as ShieldResponse
      const sessionId = deviceIntelligence.result.session_id

      const serverResponse = await sendAttributesToServer({
        session_id: sessionId,
        user_id: userId.toString(),
      })

      setShieldState(prev => ({ ...prev, isInitialized: true, isLoading: false }))
      return serverResponse
    } catch (error) {
      const shieldError = error as ShieldError
      console.error('Shield error:', {
        message: shieldError.message,
        response: shieldError.response,
      })
      setShieldState(prev => ({ ...prev, isLoading: false, error: shieldError }))
      throw error
    }
  }, [userId])

  useEffect(() => {
    if (envMode === DEPLOY_ENVIRONMENT.PRODUCTION && isShieldFlipperEnabled?.enabled) {
      initializeShield().catch(error => {
        console.error('Failed to initialize Shield:', error)
      })
    }

    return () => {
      shieldRef.current = null
    }
  }, [initializeShield, isShieldFlipperEnabled])

  return {
    ...state,
    initialize: initializeShield,
    isEnabled: isShieldFlipperEnabled,
  }
}
